import React, { FC, useContext } from 'react';
import styled from 'styled-components';
import { pxToRem } from '../utils/pxToRem';
import { contentfulPageKontakt } from '../model/generated/graphql';
import { Container } from './Container/Container';
import FacebookIcon from '../../static/icons/facebook.svg';
import InstagramIcon from '../../static/icons/instagram.svg';
import { Link, Script } from 'gatsby';
import { rem } from 'polished';
import { ROUTES } from '../utils/routes';
import LocaleContext from '../context/Locale';

export const Footer: FC<{ data: { contentfulPageKontakt: contentfulPageKontakt } }> = ({ data }) => {
    const { activeLocale } = useContext(LocaleContext);
    const locale = activeLocale === 'en' ? 'en' : 'pl';

    return (
        <Wrapper>
            <Container>
                <Inner>
                    <LinksGroup>
                        <Copyright>MAGMED © {new Date().getFullYear()}</Copyright>
                        <FooterLink to={`/${locale}${ROUTES.PRIVACY}`}>
                            Polityka prywatności
                        </FooterLink>
                        <FooterLink to={`/${locale}${ROUTES.ACCESSIBILITY}`}>
                            Deklaracja dostępności
                        </FooterLink>
                        <FooterLink to={`/${locale}${ROUTES.YOUTH_PROTECT}`}>
                            Standardy ochrony małoletnich
                        </FooterLink>
                    </LinksGroup>

                    <SocialGroup>
                        {data.contentfulPageKontakt.facebookLink && data.contentfulPageKontakt.facebookLink !== ' ' && (
                            <SocialLink
                                href={data.contentfulPageKontakt.facebookLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Facebook MAGMED (otwiera w nowej karcie)"
                            >
                                <FacebookIcon width={32} fill="#fff" aria-hidden="true" />
                            </SocialLink>
                        )}
                        {data.contentfulPageKontakt.instagramLink && data.contentfulPageKontakt.instagramLink !== ' ' && (
                            <SocialLink
                                href={data.contentfulPageKontakt.instagramLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Instagram MAGMED (otwiera w nowej karcie)"
                            >
                                <InstagramIcon width={32} fill="#fff" aria-hidden="true" />
                            </SocialLink>
                        )}
                    </SocialGroup>

                    <Script id="mydr" strategy="idle">
                        {`const mydrScript = document.getElementsByTagName('script')[0];
const js = document.createElement('script');
js.src = "https://mydr.pl/static/mydr-pp.min.js";
mydrScript.parentNode.insertBefore(js, mydrScript);
js.onload = () => {
const PatientsPlugin = new window.PatientsPlugin;
PatientsPlugin.init({
app: 'https://mydr.pl/patients_plugin',
plugin: 'https://mydr.pl/static',
});
};`}
                    </Script>
                </Inner>
            </Container>
        </Wrapper>
    );
};

const Wrapper = styled.footer`
    position: relative;
    overflow: hidden;
    width: 100%;
    padding: ${pxToRem(40)};
    border-top: 4px solid var(--color-secondary);
    @media (max-width: ${pxToRem(1000)}) {
        padding-bottom: ${pxToRem(80)};
    }
    background-color: var(--color-primary);
    color: var(--color-white);
    margin-top: auto;
    box-sizing: border-box;

    /* Decorative background circles */
    &::before,
    &::after {
        content: '';
        position: absolute;
        border-radius: 50%;
        pointer-events: none;
    }
    &::before {
        width: ${pxToRem(320)};
        height: ${pxToRem(320)};
        top: ${pxToRem(-120)};
        right: ${pxToRem(-80)};
        background: rgba(255, 255, 255, 0.04);
    }
    &::after {
        width: ${pxToRem(200)};
        height: ${pxToRem(200)};
        bottom: ${pxToRem(-80)};
        left: ${pxToRem(-50)};
        background: rgba(255, 255, 255, 0.04);
    }
`;

const Inner = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${pxToRem(24)};

    @media (max-width: ${rem(720)}) {
        flex-direction: column;
        align-items: center;
        gap: ${pxToRem(16)};
    }
`;

const LinksGroup = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: ${pxToRem(8)} ${pxToRem(20)};

    @media (max-width: ${rem(720)}) {
        justify-content: center;
        order: 2;
    }
`;

const Copyright = styled.span`
    font-weight: 600;
    white-space: nowrap;
`;

const FooterLink = styled(Link)`
    color: var(--color-white);
    text-decoration: none;
    font-size: ${pxToRem(14)};
    white-space: nowrap;
    opacity: 0.85;
    transition: opacity 0.15s ease;

    &:hover {
        opacity: 1;
        text-decoration: underline;
    }
    &:focus-visible {
        outline: 2px solid var(--color-white);
        outline-offset: 3px;
        border-radius: 2px;
        opacity: 1;
    }
`;

const SocialGroup = styled.div`
    display: flex;
    align-items: center;
    gap: ${pxToRem(8)};
    flex-shrink: 0;

    @media (max-width: ${rem(720)}) {
        order: 1;
    }
`;

const SocialLink = styled.a`
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: ${pxToRem(44)};
    height: ${pxToRem(44)};
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.25);
    transition: background-color 0.15s ease, border-color 0.15s ease;

    &:hover {
        background-color: rgba(255, 255, 255, 0.12);
        border-color: rgba(255, 255, 255, 0.5);
    }
    &:focus-visible {
        outline: 2px solid var(--color-white);
        outline-offset: 3px;
        border-radius: 50%;
    }
`;
